import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, AlertCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Sign-in, rendered on this origin.
//
// Previously this page was only reachable in local-mode deployments —
// hub mode redirected the whole browser to hub.estreamly.co. That is the
// thing that made signing in from the installed PWA so painful: the
// navigation leaves the app's manifest scope, so the standalone window
// hands off to a browser, and on iOS that browser doesn't share the
// home-screen app's cookie jar. Users signed in successfully and came
// back to a still-signed-out app.
//
// Now the form posts to the hub's JSON auth API and stores the tokens
// itself. Nothing navigates.
//
// Styling is the Organic system (cream, terracotta, Caprasimo) that
// every screen behind the login already uses — this was the last page
// left on the old indigo gradient, which made the first screen a user
// sees the one that looks least like the product.
//
// The default path is email + a 6-digit code, because most accounts are
// passwordless (`auth_method='magic_link'` on the hub). The code field
// is marked up for one-time-code autofill so iOS and Android offer the
// code from the notification instead of making the user switch to their
// mail app and copy it.

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;
const STROKE = 2.75;   // matches the rail's lucide weight

function Field({ id, label, children }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children}
    </div>
  );
}

function Login() {
  // 'email' → collect the address; 'code' → collect the emailed code;
  // 'password' → the alternative for accounts that have one;
  // 'forgot' → email a link to set a new password (hub accounts only).
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [cooldown, setCooldown] = useState(0);

  const {
    login, requestCode, verifyCode, requestPasswordReset, error, setError, loginMode,
  } = useAuth();
  const navigate = useNavigate();
  const codeRef = useRef(null);

  const isLocalMode = loginMode === 'local';
  const identityLabel = isLocalMode ? 'Username' : 'Work email';
  const identityPlaceholder = isLocalMode ? 'Enter your username' : 'you@company.com';

  // Tick the resend cooldown down. Stops the user hammering the send
  // endpoint into the hub's per-address rate limit.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  useEffect(() => {
    if (step === 'code' && codeRef.current) codeRef.current.focus();
  }, [step]);

  const resetMessages = () => {
    setNotice(null);
    if (setError) setError(null);
  };

  const handleSendCode = async (e) => {
    if (e) e.preventDefault();
    resetMessages();
    setBusy(true);
    const sent = await requestCode(email);
    setBusy(false);
    if (sent) {
      setStep('code');
      setCode('');
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setNotice(`We sent a ${CODE_LENGTH}-digit code to ${email.trim().toLowerCase()}.`);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    resetMessages();
    setBusy(true);
    const ok = await verifyCode(email, code);
    setBusy(false);
    if (ok) navigate('/');
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    resetMessages();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) navigate('/');
  };

  // The reset link lands on the hub's own page (a plain form that works
  // in any browser). Once the password is set the user comes back here
  // to sign in, so leave them on the password step with a clear note.
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    resetMessages();
    setBusy(true);
    const sent = await requestPasswordReset(email);
    setBusy(false);
    if (sent) {
      setStep('password');
      setPassword('');
      setNotice(
        `If ${email.trim().toLowerCase()} has an account, we emailed it a link to choose a new password. `
        + 'Set it there, then sign in here with the new password.',
      );
    }
  };

  const identityInput = (id, autoFocus) => (
    <input
      id={id}
      className="input"
      name="email"
      type={isLocalMode ? 'text' : 'email'}
      value={email}
      onChange={(e) => setEmail(e.target.value)}
      placeholder={identityPlaceholder}
      required
      autoFocus={autoFocus}
      autoComplete={isLocalMode ? 'username' : 'email'}
      inputMode={isLocalMode ? 'text' : 'email'}
      autoCapitalize="none"
      autoCorrect="off"
      spellCheck="false"
    />
  );

  return (
    <div className="organic o-auth">
      <div className="o-auth-card">
        <div className="o-auth-head">
          <span className="o-auth-mark">
            <Activity size={24} strokeWidth={STROKE} />
          </span>
          <h1 className="o-auth-title">Lead Discovery</h1>
          <p className="o-auth-sub">
            {step === 'code' && 'Enter the code we emailed you'}
            {step === 'forgot' && "We'll email you a link to choose a new password"}
            {step !== 'code' && step !== 'forgot' && 'Sign in to access your dashboard'}
          </p>
        </div>

        {error && (
          <div className="o-auth-msg o-auth-msg-error" role="alert">
            <AlertCircle size={16} strokeWidth={STROKE} />
            <span>{error}</span>
          </div>
        )}

        {notice && !error && (
          <div className="o-auth-msg o-auth-msg-info" role="status">
            <span>{notice}</span>
          </div>
        )}

        {/* ── Step 1: email ─────────────────────────────────────── */}
        {step === 'email' && (
          <form className="o-auth-form" onSubmit={handleSendCode}>
            <Field id="login-email" label={identityLabel}>
              {identityInput('login-email', true)}
            </Field>

            {isLocalMode ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setStep('password')}
              >
                Continue
              </button>
            ) : (
              <button type="submit" className="btn btn-primary" disabled={busy || !email}>
                {busy ? 'Sending…' : 'Email me a code'}
              </button>
            )}

            {!isLocalMode && (
              <div className="o-auth-alt">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => { resetMessages(); setStep('password'); }}
                >
                  Use a password instead
                </button>
              </div>
            )}
          </form>
        )}

        {/* ── Step 2: emailed code ──────────────────────────────── */}
        {step === 'code' && (
          <form className="o-auth-form" onSubmit={handleVerifyCode}>
            <Field id="login-code" label={`${CODE_LENGTH}-digit code`}>
              <input
                id="login-code"
                ref={codeRef}
                className="input o-auth-code"
                name="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
                placeholder="000000"
                required
                // The three attributes that let iOS and Android offer the
                // code straight from the notification, so the user never
                // has to leave the app for their mail client.
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={CODE_LENGTH}
              />
            </Field>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={busy || code.length < CODE_LENGTH}
            >
              {busy ? 'Verifying…' : 'Sign in'}
            </button>

            <div className="o-auth-row">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => { resetMessages(); setStep('email'); }}
              >
                <ArrowLeft size={14} strokeWidth={STROKE} /> Change email
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleSendCode}
                disabled={busy || cooldown > 0}
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
              </button>
            </div>
          </form>
        )}

        {/* ── Password ──────────────────────────────────────────── */}
        {step === 'password' && (
          <form className="o-auth-form" onSubmit={handlePasswordLogin}>
            <Field id="login-email-pw" label={identityLabel}>
              {identityInput('login-email-pw', false)}
            </Field>

            <Field id="login-password" label="Password">
              <input
                id="login-password"
                className="input"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                autoFocus
                autoComplete="current-password"
              />
            </Field>

            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>

            {!isLocalMode && (
              <div className="o-auth-row">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => { resetMessages(); setStep('email'); }}
                >
                  Email me a code instead
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => { resetMessages(); setStep('forgot'); }}
                >
                  Forgot password?
                </button>
              </div>
            )}
          </form>
        )}

        {/* ── Forgot password ───────────────────────────────────── */}
        {step === 'forgot' && (
          <form className="o-auth-form" onSubmit={handleForgotPassword}>
            <Field id="login-email-forgot" label={identityLabel}>
              {identityInput('login-email-forgot', true)}
            </Field>

            <button type="submit" className="btn btn-primary" disabled={busy || !email}>
              {busy ? 'Sending…' : 'Email me a reset link'}
            </button>

            <div className="o-auth-alt">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => { resetMessages(); setStep('password'); }}
              >
                <ArrowLeft size={14} strokeWidth={STROKE} /> Back to sign in
              </button>
            </div>
          </form>
        )}
      </div>

      <p className="o-auth-footer">Social Listener, powered by eStreamly &mdash; 2026</p>
    </div>
  );
}

export default Login;
